import React, { useState, forwardRef, useImperativeHandle } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { Stack, FormControlLabel, Switch, Typography, Modal, Box, IconButton } from '@mui/material';
import { useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { ADD_WORKING_HOUR_OVERRIDE_GROUP, UPDATE_WORKING_HOUR_OVERRIDE_GROUP } from '../../graphql/mutations';

// For display conversions on edit
import { utcToZonedTime } from 'date-fns-tz';

// Helper function to convert a stored UTC time string ("HH:mm")
// into a Dayjs object in the provider's local time.
const convertUTCToLocalDayjs = (utcTimeStr, providerTZ) => {
    if (!utcTimeStr) return null;
    const utcDate = new Date(`1970-01-01T${utcTimeStr}:00Z`);
    const localDate = utcToZonedTime(utcDate, providerTZ);
    return dayjs(localDate);
};

// Modal style
const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    overflow: 'auto',
};

// Helper to normalize a Dayjs time object to a fixed reference date (only hour and minute matter)
const normalizeTime = (time) => {
    return dayjs().startOf('day').set('hour', time.hour()).set('minute', time.minute());
};

const Overrides = forwardRef((props, ref) => {
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState('');
    // Override's start and end dates (local Dayjs objects)
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [isFullDay, setIsFullDay] = useState(true);
    // Array of time ranges (each with startTime and endTime as Dayjs objects)
    const [timeRanges, setTimeRanges] = useState([]);

    // Editing state
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null); 

    const [addWorkingHourOverrideGroup] = useMutation(ADD_WORKING_HOUR_OVERRIDE_GROUP);
    const [updateWorkingHourOverrideGroup] = useMutation(UPDATE_WORKING_HOUR_OVERRIDE_GROUP);

    const [errors, setErrors] = useState({ dates: '', times: [] });

    const validateDates = (start, end) => {
        const today = dayjs().startOf('day');
        let error = '';
        if (start && start.isBefore(today)) {
            error = 'Start date cannot be in the past';
        } else if (end && end.isBefore(start)) {
            error = 'End date must be after start date';
        }
        setErrors(prev => ({ ...prev, dates: error }));
        return !error;
    };
    // Updated toggle handler to initialize time ranges with valid Dayjs objects
    // Updated toggle handler: use dayjs('') for empty time fields
    const updateFullDayStatus = (e) => {
    const fullDayStatus = !e.target.checked; // full day mode if switch is off
    setIsFullDay(fullDayStatus);
    if (fullDayStatus) {
        setTimeRanges([{ id: null, startTime: "All Day", endTime: "All Day" }]);
    } else {
        // Use dayjs('') to represent an empty/invalid time so that the picker shows an empty field.
        setTimeRanges([{ id: null, startTime: dayjs(''), endTime: dayjs('') }]);
    }
    };



    // Updated validateTimeRanges: normalizes times to a fixed reference date before comparison.
    const validateTimeRanges = (ranges) => {
        const newErrors = ranges.map((range) => {
            if (!range.startTime || !range.endTime) return '';
            // Normalize times to the same reference date
            const normStart = normalizeTime(range.startTime);
            const normEnd = normalizeTime(range.endTime);
            if (normEnd.isBefore(normStart)) return 'End time must be after start time';
            // Check overlapping with other ranges
            for (let i = 0; i < ranges.length; i++) {
                if (ranges[i] && ranges[i] !== range && ranges[i].startTime && ranges[i].endTime) {
                    const otherStart = normalizeTime(ranges[i].startTime);
                    const otherEnd = normalizeTime(ranges[i].endTime);
                    if (
                        normStart.isBetween(otherStart, otherEnd, null, '[]') ||
                        normEnd.isBetween(otherStart, otherEnd, null, '[]') ||
                        (normStart.isBefore(otherStart) && normEnd.isAfter(otherEnd))
                    ) {
                        return 'Time ranges cannot overlap';
                    }
                }
            }
            return '';
        });
        setErrors(prev => ({ ...prev, times: newErrors }));
        return !newErrors.some(err => err);
    };

    // Open modal for add or edit. On edit, convert stored UTC dates/times to local Dayjs objects.
    const handleOpen = (override = null) => {
        if (override) {
            const count = override?.workingHourOverrides && override.workingHourOverrides.length
            const hasTimeRanges = count > 0 ? (count === 1 && override?.workingHourOverrides && override.workingHourOverrides[0].startTime.toLowerCase() === "all day" ? false : true) : false;
            setIsEditing(true);
            setEditingId(override.id);
            setTitle(override.title);
            const userTimeZone = props.providerUser?.timeZone?.timezone; // e.g., "America/Los_Angeles"
            if (userTimeZone) {
                setStartDate(dayjs(utcToZonedTime(new Date(override.startDate), userTimeZone)));
                setEndDate(dayjs(utcToZonedTime(new Date(override.endDate), userTimeZone)));
            } else {
                setStartDate(dayjs(new Date(override.startDate)));
                setEndDate(dayjs(new Date(override.endDate)));
            }
            setIsFullDay(hasTimeRanges ? false : true);
            setTimeRanges(
                hasTimeRanges
                    ? override.workingHourOverrides.map(item => ({
                        id: Number(item?.id),
                        startTime: convertUTCToLocalDayjs(item.startTime, userTimeZone),
                        endTime: convertUTCToLocalDayjs(item.endTime, userTimeZone)
                    }))
                    : [{ startTime: null, endTime: null }]
            );
        } else {
            resetForm();
        }
        setOpen(true);
    };

    useImperativeHandle(ref, () => ({ handleOpen }));

    // Updated add time range: initialize with empty Dayjs objects
    const handleAddTimeRange = () => {
    setTimeRanges([
        ...timeRanges,
        { id: null, startTime: dayjs(''), endTime: dayjs('') }
    ]);
};


    const handleRemoveTimeRange = (index) => {
        const updatedRanges = timeRanges.filter((_, i) => i !== index);
        setTimeRanges(updatedRanges);
    };

    const handleTimeChange = (index, type, newTime) => {
        const newTimeRanges = [...timeRanges];
        newTimeRanges[index][type] = newTime;
        setTimeRanges(newTimeRanges);
        validateTimeRanges(newTimeRanges);
    };

    const resetForm = () => {
        setTitle('');
        setStartDate(null);
        setEndDate(null);
        setIsFullDay(true);
        setTimeRanges([{ id: null, startTime: "All Day", endTime: "All Day" }]);
        setIsEditing(false);
        setEditingId(null); 
    };

    const handleClose = () => {
        setOpen(false);
        resetForm();
    };

    // On submit, send local override dates and times as strings along with timeZoneId.
    // The backend will convert these local strings to UTC.
    // Ensure you have imported and extended dayjs with the UTC plugin somewhere in your project:
    // import dayjs from 'dayjs';
    // import utc from 'dayjs/plugin/utc';
    // dayjs.extend(utc);

    const handleSubmit = async () => {
        // Validate date fields first
        const datesValid = validateDates(startDate, endDate);
        // Only run time validations if not full day
        const timesValid = isFullDay || validateTimeRanges(timeRanges);
        if (!datesValid || !timesValid) return;

        const groupId = Number(props.staffData?.workingHourGroups?.[0]?.id) || null;

        // Convert dates to ISO UTC strings at the start of the day.
        const localStartDateStr = startDate.startOf('day').format("YYYY-MM-DD[T]HH:mm:ssZ");
        const localEndDateStr = endDate.startOf('day').format("YYYY-MM-DD[T]HH:mm:ssZ");

        const commonVariables = {
            input: {
                groupId,
                title,
                startDate: localStartDateStr,
                endDate: localEndDateStr,
                timeZoneId: props.providerUser?.timeZone?.id,
            }
        };

        if (isEditing) {
            commonVariables.input.updateId = Number(editingId);
        }

        // For non-full day mode, verify that each time range is valid
        const overridesArray = timeRanges.map(range => {
            if (isFullDay) {
                return {
                    groupId,
                    startTime: 'All Day',
                    endTime: 'All Day',
                };
            } else {
                // Check that the times exist and are valid Dayjs objects
                if (
                    !range.startTime ||
                    !range.endTime ||
                    !range.startTime.isValid ||
                    !range.startTime.isValid() ||  // Extra check to ensure isValid exists
                    !range.startTime.isValid() ||
                    !range.endTime.isValid()
                ) {
                    // Instead of throwing, update the errors state and stop submission
                    setErrors(prev => ({ ...prev, times: ["Invalid time selected"] }));
                    return null;
                }
                const localStartStr = range.startTime.format("hh:mm a");
                const localEndStr = range.endTime.format("hh:mm a");
                return {
                    groupId,
                    id: Number(range.id),
                    startTime: localStartStr,
                    endTime: localEndStr,
                };
            }
        });

        // If any time range returned null, then we stop
        if (!overridesArray.every(range => range !== null)) return;

        const variables = {
            ...commonVariables,
            input: {
                ...commonVariables.input,
                normalWorkingHourOverrides: overridesArray,
            },
        };

        try {
            if (isEditing) {
                await updateWorkingHourOverrideGroup({ variables });
            } else {
                await addWorkingHourOverrideGroup({ variables });
            }
            props.refetchOverrides(); 
            handleClose();
        } catch (error) {
            console.error("Error creating/updating override record:", error);
        }
    };



    const handleStartDateChange = (newDate) => {
        setStartDate(newDate);
        validateDates(newDate, endDate);
    };

    const handleEndDateChange = (newDate) => {
        setEndDate(newDate);
        validateDates(startDate, newDate);
    };

    const isDateRangeValid = () => startDate && endDate && !errors.dates;
    const canEnableTimeSpecification = () => title && isDateRangeValid();
    const isTimeSlotValid = (timeRange) =>
        timeRange.startTime && timeRange.endTime && !errors.times[timeRanges.indexOf(timeRange)];

    return (
        <div>
            <Button variant="contained" onClick={() => handleOpen()}>
                Add Override
            </Button>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-modal-title"
                disableEnforceFocus
                disableAutoFocus
            >
                <Box sx={modalStyle}>
                    <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                        {isEditing ? 'Edit Override' : 'Add Override'} to update availability
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Stack spacing={3}>
                            <TextField
                                label="Title"
                                variant="outlined"
                                fullWidth
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                            />
                            <Stack direction="row" spacing={2}>
                                <DatePicker
                                    label="Start Date"
                                    value={startDate}
                                    onChange={handleStartDateChange}
                                    minDate={dayjs().startOf('day')}
                                    slotProps={{ textField: { fullWidth: true, error: !!errors.dates, helperText: errors.dates } }}
                                />
                                <DatePicker
                                    label="End Date"
                                    value={endDate}
                                    onChange={handleEndDateChange}
                                    minDate={startDate || dayjs().startOf('day')}
                                    slotProps={{ textField: { fullWidth: true, error: !!errors.dates, helperText: errors.dates } }}
                                />
                            </Stack>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={!isFullDay}
                                        onChange={(e) => updateFullDayStatus(e)}
                                        disabled={!canEnableTimeSpecification()}
                                    />
                                }
                                label="Specify Time Range"
                            />
                            {!isFullDay && (
                                <>
                                    {timeRanges.map((range, index) => (
                                        <Stack key={index} spacing={1}>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <TimePicker
                                                    label="Start Time"
                                                    value={range.startTime}
                                                    onChange={(newTime) => handleTimeChange(index, 'startTime', newTime)}
                                                    slotProps={{ textField: { fullWidth: true, error: !!errors.times[index], helperText: errors.times[index] } }}
                                                />
                                                <TimePicker
                                                    label="End Time"
                                                    value={range.endTime}
                                                    onChange={(newTime) => handleTimeChange(index, 'endTime', newTime)}
                                                    slotProps={{ textField: { fullWidth: true, error: !!errors.times[index], helperText: errors.times[index] } }}
                                                />
                                                {index === timeRanges.length - 1 && (
                                                    <IconButton onClick={handleAddTimeRange} color="primary" disabled={!isTimeSlotValid(range)}>
                                                        <AddCircleIcon />
                                                    </IconButton>
                                                )}
                                                {timeRanges.length > 1 && (
                                                    <IconButton onClick={() => handleRemoveTimeRange(index)} color="secondary">
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}
                                            </Stack> 
                                        </Stack>
                                    ))}
                                </>
                            )}
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <Button onClick={handleClose}>Cancel</Button>
                                <Button variant="contained" onClick={handleSubmit}>
                                    Submit
                                </Button>
                            </Stack>
                        </Stack>
                    </LocalizationProvider>
                </Box>
            </Modal>
        </div>
    );
});

export default Overrides;
